import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useCollapsibleState } from "../hooks/use-collapsible-state.js";
import { useFormStoreSelector, useForms } from "../hooks/use-form-store.js";
import { useSchemaContext } from "../hooks/use-schema-context.js";
import { getChildFieldResolved } from "../utils/field-helpers.js";
import { joinPath } from "../utils/path.js";
import { getShapeFormConfig } from "../utils/refs.js";
import { getPropertyNames, resolveRef } from "../utils/schema-context.js";
import { CollapsibleHeader } from "./internal/collapsible-header.js";
// biome-ignore lint/nursery/noImportCycles: Intentional cycle for recursive form rendering (ObjectField -> ShapeField -> ObjectField)
import { ShapeField } from "./shape-field.js";
/**
 * Renders object properties, each as a ShapeField.
 *
 * This is the single implementation for object property enumeration.
 * Used both as a public entry point (<ObjectField schema={...} />) and
 * internally by ShapeField when it encounters an object schema.
 *
 * Handles presentation: title headers, collapsible sections, noWrapper.
 */
export function ObjectField({ path = '', schema, include: includeProp, exclude: excludeProp, order: orderProp, className, config, disabled, readOnly, parentRef: parentRefProp, title, required, isCollapsible, collapseByDefault, noWrapper }) {
    const ctx = useSchemaContext();
    const formConfig = useFormStoreSelector((s) => s.config);
    const rootSchema = useFormStoreSelector((s) => s.schema);
    const forms = useForms();
    // Use provided schema, or fall back to root schema for the root path
    const effectiveSchema = schema ?? (path === '' ? rootSchema : undefined);
    const resolvedSchema = useMemo(() => (effectiveSchema ? resolveRef(ctx, effectiveSchema) : undefined), [ctx, effectiveSchema]);
    // Extract the original $ref before resolution (for shape-based config lookup)
    const schemaRef = parentRefProp ?? effectiveSchema?.$ref;
    // Get shape-based form config (from forms registry using $ref)
    const shapeConfig = useMemo(() => getShapeFormConfig(forms, schemaRef), [forms, schemaRef]);
    // Merge config sources: shape config as base, explicit config overrides, props override all
    const include = includeProp ?? config?.include;
    const exclude = excludeProp ?? config?.exclude;
    const configOrder = orderProp ?? (Array.isArray(config?.order) ? config.order : undefined) ?? shapeConfig?.order;
    // Collapsible state (always call for hooks rules)
    const { isOpen, toggle } = useCollapsibleState(path, collapseByDefault);
    const properties = useMemo(() => {
        if (!resolvedSchema)
            return [];
        let props = getPropertyNames(ctx, resolvedSchema);
        // Filter by include list
        if (include) {
            props = props.filter((p) => include.includes(p));
        }
        // Filter by exclude list
        if (exclude) {
            props = props.filter((p) => !exclude.includes(p));
        }
        // Filter by hidden config (check path-based, config-based, and shape-based)
        props = props.filter((p) => {
            const fieldPath = path ? joinPath(path, p) : p;
            if (formConfig?.[fieldPath]?.hidden)
                return false;
            if (config?.properties?.[p]?.hidden)
                return false;
            if (shapeConfig?.properties?.[p]?.hidden)
                return false;
            return true;
        });
        // Order: config.order / shape.order acts as whitelist filter+sort
        if (configOrder) {
            return configOrder.filter((p) => props.includes(p));
        }
        // Fallback: path-based ordering from store config
        if (formConfig) {
            return [...props].sort((a, b) => {
                const aPath = path ? joinPath(path, a) : a;
                const bPath = path ? joinPath(path, b) : b;
                const aRaw = formConfig[aPath]?.order;
                const aOrder = typeof aRaw === 'number' ? aRaw : Number.MAX_SAFE_INTEGER;
                const bRaw = formConfig[bPath]?.order;
                const bOrder = typeof bRaw === 'number' ? bRaw : Number.MAX_SAFE_INTEGER;
                return aOrder - bOrder;
            });
        }
        return props;
    }, [ctx, resolvedSchema, include, exclude, configOrder, formConfig, config, shapeConfig, path]);
    if (!resolvedSchema) {
        if (effectiveSchema)
            return null;
        console.warn(`ObjectField: No schema provided and cannot resolve from path "${path}"`);
        return null;
    }
    // Determine config source for child properties
    const childConfigSource = config?.properties ?? shapeConfig?.properties;
    const propertiesContent = (_jsx("div", { className: `space-y-4 ${className ?? ''}`, children: properties.map((prop) => (_jsx(ShapeField, { ...getChildFieldResolved(ctx, resolvedSchema, path, prop), parentRef: schemaRef, config: childConfigSource?.[prop], disabled: disabled, readOnly: readOnly }, prop))) }));
    // Collapsible object section
    if (isCollapsible && title) {
        return (_jsxs("div", { className: "border border-base-300 rounded-box mt-1", children: [_jsx(CollapsibleHeader, { isOpen: isOpen, onToggle: toggle, className: "flex items-center gap-1 px-3 py-2 bg-base-200 rounded-box", title: _jsxs(_Fragment, { children: [title, required && _jsx("span", { className: "text-error text-xs ml-0.5", children: "*" })] }) }), isOpen && _jsx("div", { className: "p-2", children: propertiesContent })] }));
    }
    // Title header (non-collapsible)
    if (title && !noWrapper) {
        return (_jsxs("div", { className: "pt-3", children: [_jsx("div", { className: "flex items-center", children: _jsxs("h4", { className: "text-sm font-semibold text-base-content/85", children: [title, required && _jsx("span", { className: "text-error text-xs ml-0.5", children: "*" })] }) }), propertiesContent] }));
    }
    return propertiesContent;
}
